<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { type QInput, QOptionGroup } from 'quasar';
import type { EmailSelectionOption, NewFlightSchoolAdministrator } from '@/types/FlightSchoolAdmin';
import AdminRepository from '@/repositories/AdminRepository';
import type FlightSchool from '@/types/FlightSchool';
import { useAdminStore } from '@/stores/admins';

const props = defineProps<{
    school: FlightSchool,
    admin: NewFlightSchoolAdministrator;
    index: number;
}>();

const adminStore = useAdminStore();

const email = toRef(props.admin, 'email');
const emailSubscriber = toRef(props.admin, 'emailSubscriber');

const emailSelectionOptions: EmailSelectionOption[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
];

const formName = computed(() => `Admin ${props.index}`);

const emailInput = ref<QInput>();

const ruleEmailAddress = (val:string) => (val.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) !== null || 'Invalid email address.');
const ruleNotDuplicateEmail = (val: string) => (
    !adminStore.isDuplicate(props.admin.id, val) || 'This email is in the list multiple times. Please remove the duplicate email(s) before saving.'
);

type CheckedEmails = {
    email: string;
    valid: boolean;
    reason?: string;
};

const checkedEmails = ref<CheckedEmails[]>([]);

// validate that an account with the given email exists
const ruleAccountExists = async (val: string) => {
    let message = `${val} cannot be assigned to this school. Please double check the address and try again.`;

    let checkedEmail = checkedEmails.value.find((input) => input.email === val);
    if (!checkedEmail) {
        const { valid, reason } = await AdminRepository.validateAccountExists(val, props.school);
        checkedEmail = {
            email: val,
            valid,
            reason,
        };

        checkedEmails.value.push(checkedEmail);
    }

    if (checkedEmail?.reason === 'Account not found.') {
        message = `Gleim account not found. A Gleim account must be created for ${val} at `
            + 'gleim.com/account before adding them to your school.';
    }

    return checkedEmail.valid || message;
};

const validate = async () => {
    const valid = await emailInput.value?.validate();

    adminStore.updateAdmin({
        ...props.admin,
        email: email.value,
        emailSubscriber: emailSubscriber.value,
        valid: valid || false,
    });
};

// expose validate as a public method
defineExpose({
    validate,
});
</script>

<template>
    <div class="container admin-form">
        <div class="column">
            <div class="row justify-between">
                <h3
                    class="form-header"
                    :class="{ 'q-mt-none': index === 1 }"
                >
                    {{ formName }}
                </h3>
                <q-btn
                    v-if="index > 1"
                    flat
                    label="Remove"
                    no-caps
                    class="q-pa-none anchor-button remove-btn"
                    @click="adminStore.removeAdminById(admin.id)"
                />
            </div>
            <q-input
                ref="emailInput"
                v-model="email"
                type="email"
                label="Email"
                debounce="500"
                outlined
                :rules="[ruleEmailAddress, ruleAccountExists, ruleNotDuplicateEmail]"
                class="q-mb-sm"
            />
            <div class="text-left">
                <span class="subscribed-text">Subscribed to Emails?</span>
                <q-option-group
                    v-model="emailSubscriber"
                    :options="emailSelectionOptions"
                    inline
                    class="q-my-md"
                    size="sm"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.form-header {
    font-size: 24px;
    font-weight: 500;
    line-height: normal;
    color: var(--fsum-dark-blue2);
}
.remove-btn {
    color: var(--fsum-blue2);
}
.admin-form:not(:last-child) .column{
    border-bottom: 1px solid var(--fsum-gray5);
}
.subscribed-text {
    font-size: 16px;
    font-weight: bold;
}
</style>
